/**
 * Tiny pub/sub bridge so the SiteHeader can react to the scroll-film without
 * prop drilling. The film publishes whether it is mounted (`active`) and whether
 * the user has scrolled to the end (`finished`). The header suppresses its solid
 * background while a film is active but unfinished, then fades it in.
 *
 * Kept free of three.js/lenis imports so the always-loaded header can import it
 * without pulling the heavy film chunk into the main bundle. Only the scrubbed
 * film (`FilmScroll`) ever sets `active`, so the static fallback (reduced motion
 * / no WebGL) leaves the header in its normal solid-on-scroll behaviour.
 */
export interface FilmNavState {
  active: boolean;
  finished: boolean;
}

let state: FilmNavState = { active: false, finished: false };
const listeners = new Set<(s: FilmNavState) => void>();

function notify() {
  for (const cb of listeners) cb(state);
}

export function getFilmNavState(): FilmNavState {
  return state;
}

export function setFilmActive(active: boolean): void {
  if (state.active === active) return;
  // Leaving a film page resets `finished` so the next film starts hidden again.
  state = { active, finished: active ? state.finished : false };
  notify();
}

export function setFilmFinished(finished: boolean): void {
  if (state.finished === finished) return;
  state = { ...state, finished };
  notify();
}

export function subscribeFilmNav(cb: (s: FilmNavState) => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}
